import React from 'react';
import { FaHome, FaCalculator } from 'react-icons/fa';
import { AffordabilityAnalysis as AffordabilityAnalysisType, MonthlyPaymentBreakdown } from '../types';
import { CALCULATION_DEFAULTS } from '../utils/constants';
import { formatCurrency, calculateMonthlyPaymentBreakdown } from '../utils/calculations';

interface AffordabilityAnalysisProps {
  analysis: AffordabilityAnalysisType;
  monthlyIncome: number;
}

const AffordabilityAnalysis: React.FC<AffordabilityAnalysisProps> = ({
  analysis,
  monthlyIncome
}) => {
  // Calculate payment breakdowns for each scenario
  const fhaBreakdown = React.useMemo(() => 
    calculateMonthlyPaymentBreakdown(
      analysis.fhaMaxPrice, 
      CALCULATION_DEFAULTS.fhaDownPayment, 
      monthlyIncome
    ), [analysis.fhaMaxPrice, monthlyIncome]
  );

  const conventionalBreakdown = React.useMemo(() => 
    calculateMonthlyPaymentBreakdown(
      analysis.conventionalMaxPrice, 
      CALCULATION_DEFAULTS.conventionalDownPayment, 
      monthlyIncome
    ), [analysis.conventionalMaxPrice, monthlyIncome]
  );

  const vaBreakdown = React.useMemo(() => 
    calculateMonthlyPaymentBreakdown(
      analysis.vaMaxPrice, 
      CALCULATION_DEFAULTS.vaDownPayment, 
      monthlyIncome
    ), [analysis.vaMaxPrice, monthlyIncome]
  );

  const PaymentBreakdownCard: React.FC<{ 
    title: string; 
    breakdown: MonthlyPaymentBreakdown; 
    highlight?: boolean;
  }> = ({ title, breakdown, highlight = false }) => (
    <div className={`p-4 rounded-lg border ${highlight ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
      <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Home Price:</span>
          <span className="font-medium">{formatCurrency(breakdown.homePrice)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Down Payment:</span>
          <span className="font-medium">{formatCurrency(breakdown.downPayment)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Loan Amount:</span>
          <span className="font-medium">{formatCurrency(breakdown.loanAmount)}</span>
        </div>
        
        <hr className="my-2" />
        
        <div className="flex justify-between">
          <span className="text-gray-600">Principal & Interest:</span>
          <span className="font-medium">{formatCurrency(breakdown.principalAndInterest)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Property Tax:</span>
          <span className="font-medium">{formatCurrency(breakdown.propertyTaxMonthly)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Insurance:</span>
          <span className="font-medium">{formatCurrency(breakdown.homeownersInsurance)}</span>
        </div>
        
        {breakdown.pmiOrMip > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">PMI/MIP:</span>
            <span className="font-medium">{formatCurrency(breakdown.pmiOrMip)}</span>
          </div>
        )}
        
        <hr className="my-2" />
        
        <div className="flex justify-between font-semibold">
          <span>Total Monthly Payment:</span>
          <span style={{ color: '#1e3a8a' }}>{formatCurrency(breakdown.totalMonthlyPayment)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Remaining Budget:</span>
          <span 
            className="font-medium"
            style={{ 
              color: breakdown.remainingBudget > 1000 ? '#16a34a' : 
                     breakdown.remainingBudget > 500 ? '#f59e0b' : '#dc2626' 
            }}
          >
            {formatCurrency(breakdown.remainingBudget)}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Budget Utilization:</span>
          <span 
            className="font-medium"
            style={{ 
              color: breakdown.budgetUtilization < 28 ? '#16a34a' : 
                     breakdown.budgetUtilization < 33 ? '#f59e0b' : '#dc2626' 
            }}
          >
            {breakdown.budgetUtilization.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <FaCalculator className="text-blue-600 text-xl" style={{ color: '#1e3a8a' }} />
        <h2 className="text-2xl font-semibold text-gray-900">Affordability Calculator</h2>
      </div>

      {/* Affordability Analysis */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Affordability Analysis</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Monthly Gross Income</div>
            <div className="text-xl font-semibold" style={{ color: '#1e3a8a' }}>
              {formatCurrency(analysis.monthlyGrossIncome)}
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Trade Worker Ratio (30%)</div>
            <div className="text-xl font-semibold" style={{ color: '#16a34a' }}>
              {formatCurrency(analysis.tradeWorkerRatio30)}
            </div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Conservative Ratio (25%)</div>
            <div className="text-xl font-semibold" style={{ color: '#16a34a' }}>
              {formatCurrency(analysis.conservativeRatio25)}
            </div>
          </div>
          
          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Max Monthly Payment (30%)</div>
            <div className="text-xl font-semibold" style={{ color: '#f59e0b' }}>
              {formatCurrency(analysis.maxMonthlyPayment30)}
            </div>
          </div>
        </div>
      </div>

      {/* Down Payment Scenarios */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Down Payment Scenarios</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center space-x-2 mb-2">
              <FaHome className="text-orange-600" />
              <span className="font-medium text-orange-900">FHA (3.5%)</span>
            </div>
            <div className="text-2xl font-bold text-orange-800">
              {formatCurrency(analysis.fhaMaxPrice)}
            </div>
            <div className="text-sm text-orange-600">Maximum Home Price</div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2 mb-2">
              <FaHome className="text-blue-600" />
              <span className="font-medium text-blue-900">Conventional (5%)</span>
            </div>
            <div className="text-2xl font-bold text-blue-800">
              {formatCurrency(analysis.conventionalMaxPrice)}
            </div>
            <div className="text-sm text-blue-600">Maximum Home Price</div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center space-x-2 mb-2">
              <FaHome className="text-green-600" />
              <span className="font-medium text-green-900">VA (0%)</span>
            </div>
            <div className="text-2xl font-bold text-green-800">
              {formatCurrency(analysis.vaMaxPrice)}
            </div>
            <div className="text-sm text-green-600">Maximum Home Price</div>
          </div>
        </div>
      </div>

      {/* Monthly Payment Breakdown */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Payment Breakdown</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <PaymentBreakdownCard 
            title="FHA 3.5%" 
            breakdown={fhaBreakdown}
          />
          <PaymentBreakdownCard 
            title="Conventional 5%" 
            breakdown={conventionalBreakdown}
            highlight={true}
          />
          <PaymentBreakdownCard 
            title="VA 0%" 
            breakdown={vaBreakdown}
          />
        </div>
      </div>

      {/* Key Insights */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-3">Key Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <strong>Trade Worker Advantage:</strong> Using 30% instead of the standard 28% debt-to-income ratio 
            reflects the stability and overtime potential of trade work.
          </div>
          <div>
            <strong>Best Option:</strong> {
              analysis.vaMaxPrice > analysis.conventionalMaxPrice && analysis.vaMaxPrice > analysis.fhaMaxPrice
                ? 'VA loan offers the highest purchasing power with no down payment'
                : analysis.conventionalMaxPrice > analysis.fhaMaxPrice 
                ? 'Conventional loan provides good value with 5% down'
                : 'FHA loan is most accessible with 3.5% down payment'
            }
          </div>
          <div>
            <strong>Budget Safety:</strong> Aim to keep total housing costs under 30% of gross income for financial stability.
          </div>
          <div>
            <strong>Emergency Fund:</strong> Maintain 3-6 months of expenses in savings before purchasing.
          </div>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Calculation Formulas:</h4>
        <div className="text-xs text-gray-700 font-mono space-y-1">
          <div>Max Payment = Monthly Income × 30%</div>
          <div>Monthly P&I = Loan Amount × [r(1+r)^n] / [(1+r)^n - 1]</div>
          <div>Property Tax = (Home Price × Tax Rate) / 12</div>
          <div>Insurance = (Home Price × 0.35%) / 12</div>
          <div>PMI/MIP = (Loan Amount × Rate) / 12 (if down payment &lt; 20%)</div>
        </div>
      </div>
    </div>
  );
};

export default AffordabilityAnalysis;